import { useState, useEffect } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import Navigation from "../components/Navigation";
import { Button } from "../components/ui/button";
import { FileText, ClipboardList, Calendar, RefreshCw, Loader2 } from "lucide-react";
import axios from "axios";
import { toast } from "sonner";

// Extracted components
import { PlanCard, EmptyPlansState } from "../components/plans/PlanCard";
import { WeeklyCompletionTracker } from "../components/plans/WeeklyCompletionTracker";
import { ProgressSummary } from "../components/plans/ProgressSummary";
import { APSQAssessmentDialog } from "../components/plans/APSQAssessmentDialog";
import { ManualCreatePlanDialog } from "../components/plans/ManualCreatePlanDialog";
import { EditPlanDialog } from "../components/plans/EditPlanDialog";
import { DeleteConfirmDialog } from "../components/plans/DeleteConfirmDialog";
import { PlanDetailDialog } from "../components/plans/PlanDetailDialog";
import { SharePlanDialog } from "../components/plans/SharePlanDialog";
import { SharedPlansSection } from "../components/plans/SharedPlansSection";

// Wellness trend components (same as Trends page)
import KeyMetricsCards from "../components/wellness/KeyMetricsCards";
import WellnessRadarProfile from "../components/wellness/WellnessRadarProfile";
import WellnessGrowthTrends from "../components/wellness/WellnessGrowthTrends";
import WeeklyComparison from "../components/wellness/WeeklyComparison";
import StressPerformanceCorrelation from "../components/wellness/StressPerformanceCorrelation";
import DayOfWeekPatterns from "../components/wellness/DayOfWeekPatterns";
import PersonalRecords from "../components/wellness/PersonalRecords";
import RollingAverageTrend from "../components/wellness/RollingAverageTrend";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const INITIAL_MANUAL_FORM = {
  title: "", description: "", duration_weeks: 4, plan_content: "",
  team_id: "", assigned_to: [], skill_type: "",
  difficulty_level: "intermediate", target_audience: "", focus_areas: []
};

const INITIAL_ASSESSMENT = {
  apsq_q1: 1, apsq_q2: 1, apsq_q3: 1, apsq_q4: 1,
  apsq_q5: 1, apsq_q6: 1, apsq_q7: 1, apsq_q8: 1,
  apsq_q9: 1, apsq_q10: 1,
  // ACSI-28 (0-3 scale)
  acsi_q1: 0, acsi_q2: 0, acsi_q3: 0, acsi_q4: 0,
  acsi_q5: 0, acsi_q6: 0, acsi_q7: 0, acsi_q8: 0,
  acsi_q9: 0, acsi_q10: 0, acsi_q11: 0, acsi_q12: 0,
  acsi_q13: 0, acsi_q14: 0, acsi_q15: 0, acsi_q16: 0,
  acsi_q17: 0, acsi_q18: 0, acsi_q19: 0, acsi_q20: 0,
  acsi_q21: 0, acsi_q22: 0, acsi_q23: 0, acsi_q24: 0,
  acsi_q25: 0, acsi_q26: 0, acsi_q27: 0, acsi_q28: 0,
  training_goals: [], preferred_exercise_types: [],
  time_availability_minutes: 30
};

export default function TrainingPlans({ user, onLogout }) {
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const [plans, setPlans] = useState([]);
  const [trainingPlans, setTrainingPlans] = useState([]);
  const [planCompletions, setPlanCompletions] = useState({});
  const [loading, setLoading] = useState(false);

  // Wellness trends state
  const [wellnessTrends, setWellnessTrends] = useState([]);
  const [wellnessTimeRange, setWellnessTimeRange] = useState('30');
  const [healthData, setHealthData] = useState([]);
  const [healthSummary, setHealthSummary] = useState(null);

  // Dialog visibility
  const [showAssessment, setShowAssessment] = useState(false);
  const [showManualCreate, setShowManualCreate] = useState(false);
  const [showEditPlan, setShowEditPlan] = useState(false);
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [selectedPlan, setSelectedPlan] = useState(null);

  // Assessment & form state
  const [hasCompletedAssessment, setHasCompletedAssessment] = useState(false);
  const [assessmentData, setAssessmentData] = useState(INITIAL_ASSESSMENT);
  const [manualFormData, setManualFormData] = useState(INITIAL_MANUAL_FORM);
  const [editingPlan, setEditingPlan] = useState(null);
  const [renewingPlan, setRenewingPlan] = useState(false);
  const [breathingPref, setBreathingPref] = useState("");
  const [planToDelete, setPlanToDelete] = useState(null);

  // Plan detail state
  const [activeWeek, setActiveWeek] = useState(1);

  // Sharing state
  const [showShareDialog, setShowShareDialog] = useState(false);
  const [planToShare, setPlanToShare] = useState(null);
  const [shareRefreshKey, setShareRefreshKey] = useState(0);

  // ─── Data Fetching ───────────────────────────────────────────

  const fetchPlanCompletions = async (planId, weekStartDate) => {
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(`${API}/mental-training/plan-weekly-completion/${planId}?week_start_date=${weekStartDate}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setPlanCompletions(prev => ({
        ...prev,
        [planId]: {
          completed_days: response.data.completed_days || [],
          week_start_date: weekStartDate,
          plan_id: planId
        }
      }));
    } catch (error) {
      console.error(`Failed to fetch plan completions for ${planId}:`, error);
    }
  };

  const fetchPlans = async () => {
    try {
      const token = localStorage.getItem('token');
      if (!token) return;
      const response = await axios.get(`${API}/ai/plans`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setPlans(response.data);
      setTrainingPlans(response.data || []);

      // Fetch completions in background — don't block plan display
      const today = new Date();
      const dayOfWeek = today.getDay();
      const daysToMonday = (dayOfWeek === 0 ? -6 : 1 - dayOfWeek);
      const monday = new Date(today);
      monday.setDate(today.getDate() + daysToMonday);
      monday.setHours(0, 0, 0, 0);
      const weekStart = monday.toISOString().split('T')[0];

      // Fire all completion fetches in parallel, errors handled individually
      (response.data || []).forEach(plan => {
        fetchPlanCompletions(plan.id, weekStart);
      });
    } catch (error) {
      console.error("Failed to load training plans:", error);
      if (error.response?.status === 401 || error._handled401) {
        // Token expired — don't show confusing toast
        return;
      }
      toast.error("Failed to load training plans");
    }
  };

  const checkAssessmentStatus = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(`${API}/training-plan-assessment/latest`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setHasCompletedAssessment(!!response.data);
    } catch (err) {
      setHasCompletedAssessment(false);
    }
  };

  useEffect(() => {
    fetchPlans();
    checkAssessmentStatus();

    const handleVisibilityChange = () => {
      if (!document.hidden) fetchPlans();
    };
    document.addEventListener('visibilitychange', handleVisibilityChange);
    return () => document.removeEventListener('visibilitychange', handleVisibilityChange);
  }, []);

  // Auto-open plan from URL param (e.g., coming from Goals page)
  useEffect(() => {
    const planId = searchParams.get("planId");
    if (planId && plans.length > 0) {
      const target = plans.find((p) => p.id === planId);
      if (target) {
        setSelectedPlan(target);
        setSearchParams({}, { replace: true });

        // If plan is still generating, poll for completion
        if (target.generation_status === 'generating') {
          const token = localStorage.getItem('token');
          const pollInterval = setInterval(async () => {
            try {
              const statusRes = await axios.get(`${API}/training-plans/${planId}/status`, {
                headers: { Authorization: `Bearer ${token}` }
              });
              if (statusRes.data.generation_status === 'complete') {
                clearInterval(pollInterval);
                toast.success("Your training plan is ready!");
                await fetchPlans();
              } else if (statusRes.data.generation_status === 'failed') {
                clearInterval(pollInterval);
                toast.error("Plan generation failed. Please try again.");
                await fetchPlans();
              }
            } catch (err) {
              clearInterval(pollInterval);
            }
          }, 5000);
          setTimeout(() => clearInterval(pollInterval), 180000);
        }
      }
    }
  }, [plans, searchParams]);


  // Fetch wellness trends data (same as Trends page)
  useEffect(() => {
    const fetchWellnessTrends = async () => {
      try {
        const token = localStorage.getItem('token');
        const headers = { Authorization: `Bearer ${token}` };
        const backendUrl = process.env.REACT_APP_BACKEND_URL;

        const [trendsRes, healthRes, summaryRes] = await Promise.all([
          fetch(`${backendUrl}/api/wellness/trends/individual?days=${wellnessTimeRange}`, { headers }),
          fetch(`${backendUrl}/api/health/data?days=${wellnessTimeRange}`, { headers }),
          fetch(`${backendUrl}/api/health/summary?days=${wellnessTimeRange}`, { headers }),
        ]);

        if (trendsRes.ok) setWellnessTrends(await trendsRes.json());
        if (healthRes.ok) setHealthData(await healthRes.json());
        if (summaryRes.ok) setHealthSummary(await summaryRes.json());
      } catch (err) { console.error("Failed to load wellness data:", err); }
    };
    fetchWellnessTrends();
  }, [wellnessTimeRange]);

  // ─── Assessment ──────────────────────────────────────────────

  const handleAssessmentChange = (field, value) => {
    setAssessmentData(prev => ({ ...prev, [field]: value }));
  };

  const toggleArrayValue = (field, value) => {
    setAssessmentData(prev => ({
      ...prev,
      [field]: prev[field].includes(value)
        ? prev[field].filter(item => item !== value)
        : [...prev[field], value]
    }));
  };

  const handleAssessmentSubmit = async () => {
    setLoading(true);
    try {
      const token = localStorage.getItem('token');
      const response = await axios.post(`${API}/training-plan-assessment`, assessmentData, {
        headers: { Authorization: `Bearer ${token}` }
      });
      const generatedPlan = response.data.generated_plan;
      setShowAssessment(false);
      setHasCompletedAssessment(true);

      if (generatedPlan.generation_status === 'generating') {
        toast.success("Assessment saved! Your personalized plan is being generated...");
        // Poll for completion
        const planId = generatedPlan.id;
        const pollInterval = setInterval(async () => {
          try {
            const statusRes = await axios.get(`${API}/training-plans/${planId}/status`, {
              headers: { Authorization: `Bearer ${token}` }
            });
            if (statusRes.data.generation_status === 'complete') {
              clearInterval(pollInterval);
              toast.success(`Your ${statusRes.data.duration_weeks}-week training plan is ready!`);
              await fetchPlans();
            } else if (statusRes.data.generation_status === 'failed') {
              clearInterval(pollInterval);
              toast.error("Plan generation failed. Please try again.");
              await fetchPlans();
            }
          } catch (err) {
            clearInterval(pollInterval);
          }
        }, 5000);
        // Stop polling after 3 minutes max
        setTimeout(() => clearInterval(pollInterval), 180000);
        await fetchPlans();
      } else {
        toast.success(`Assessment completed! Your personalized ${generatedPlan.duration_weeks}-week training plan has been created.`);
        await fetchPlans();
      }
    } catch (error) {
      toast.error("Failed to save assessment and generate plan");
    }
    setLoading(false);
  };

  // ─── Manual Create ───────────────────────────────────────────

  const handleManualCreatePlan = async () => {
    if (!manualFormData.title || !manualFormData.description || !manualFormData.plan_content || !manualFormData.skill_type) {
      toast.error("Please fill in all required fields (title, description, skill type, and plan content)");
      return;
    }
    setLoading(true);
    try {
      const token = localStorage.getItem('token');
      await axios.post(`${API}/training-plans/manual`, manualFormData, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success("Training plan created successfully!");
      setShowManualCreate(false);
      setManualFormData(INITIAL_MANUAL_FORM);
      fetchPlans();
    } catch { toast.error("Failed to create training plan"); }
    finally { setLoading(false); }
  };

  const handleLoadTemplate = async () => {
    try {
      setLoading(true);
      const token = localStorage.getItem('token');
      const response = await axios.get(`${API}/training-plans/template`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      if (response.data?.template) {
        setManualFormData(prev => ({ ...prev, plan_content: response.data.template }));
        toast.success("Comprehensive template loaded! Customize it for your specific needs.");
      } else {
        toast.error("Template data is empty");
      }
    } catch (error) {
      toast.error("Failed to load template: " + (error.response?.data?.detail || error.message));
    } finally { setLoading(false); }
  };

  // ─── Edit / Delete ───────────────────────────────────────────

  const canUserEditPlan = (plan) => {
    if (!user || !plan) return false;
    if (plan.user_id === user.id) return true;
    const higherPermissionRoles = ['coach', 'team-admin', 'sport-performance-consultant', 'school-admin', 'system-admin'];
    if (higherPermissionRoles.includes(plan.created_by_role)) return false;
    return true;
  };

  const handleEditPlan = (plan) => {
    setEditingPlan(plan);
    setManualFormData({
      title: plan.title, description: plan.description,
      duration_weeks: plan.duration_weeks,
      plan_content: plan.plan_content || plan.content || '',
      team_id: plan.team_id || "", assigned_to: plan.assigned_to || [],
      skill_type: plan.skill_type || "",
      difficulty_level: plan.difficulty_level || "intermediate",
      target_audience: plan.target_audience || "", focus_areas: plan.focus_areas || []
    });
    setShowEditPlan(true);
  };

  const handleUpdatePlan = async () => {
    if (!editingPlan || !manualFormData.title || !manualFormData.description || !manualFormData.plan_content) {
      toast.error("Please fill in all required fields (title, description, and plan content)");
      return;
    }
    setLoading(true);
    try {
      const token = localStorage.getItem('token');
      await axios.put(`${API}/training-plans/${editingPlan.id}`, manualFormData, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success("Training plan updated successfully!");
      setShowEditPlan(false);
      setEditingPlan(null);
      setManualFormData(INITIAL_MANUAL_FORM);
      fetchPlans();
    } catch { toast.error("Failed to update training plan"); }
    finally { setLoading(false); }
  };

  const handleDeletePlan = (plan) => {
    setPlanToDelete(plan);
    setShowDeleteConfirm(true);
  };

  const confirmDeletePlan = async () => {
    if (!planToDelete) return;
    try {
      const token = localStorage.getItem('token');
      await axios.delete(`${API}/training-plans/${planToDelete.id}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success("Training plan deleted successfully!");
      setShowDeleteConfirm(false);
      setPlanToDelete(null);
      fetchPlans();
    } catch { toast.error("Failed to delete training plan"); }
  };

  // ─── Day Completion ──────────────────────────────────────────

  const markDayComplete = async (planId, dayNumber, completed) => {
    try {
      const token = localStorage.getItem('token');
      const today = new Date();
      const dayOfWeek = today.getDay() === 0 ? 7 : today.getDay();
      const monday = new Date(today);
      monday.setDate(today.getDate() - (dayOfWeek - 1));
      const weekStartDate = monday.toISOString().split('T')[0];

      await axios.post(
        `${API}/training-plans/${planId}/complete-day`,
        { plan_id: planId, day_number: dayNumber, completed, week_start_date: weekStartDate },
        { headers: { Authorization: `Bearer ${token}` } }
      );

      // Update planCompletions locally
      setPlanCompletions(prev => {
        const planData = prev[planId] || { completed_days: [], week_start_date: weekStartDate, plan_id: planId };
        let newCompletedDays = [...planData.completed_days];
        const weekdayIndex = (dayNumber - 1) % 7;
        if (completed) {
          if (!newCompletedDays.includes(weekdayIndex)) newCompletedDays.push(weekdayIndex);
        } else {
          newCompletedDays = newCompletedDays.filter(d => d !== weekdayIndex);
        }
        return { ...prev, [planId]: { ...planData, completed_days: newCompletedDays } };
      });

      await fetchPlans();

      if (selectedPlan?.id === planId) {
        const freshResponse = await axios.get(`${API}/training-plans/${planId}`, {
          headers: { Authorization: `Bearer ${token}` }
        });
        setSelectedPlan(freshResponse.data);
      }

      const dayNames = ['Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday', 'Sunday'];
      toast.success(completed ? `${dayNames[(dayNumber - 1) % 7] || 'Day'} marked as complete!` : `${dayNames[(dayNumber - 1) % 7] || 'Day'} marked as incomplete`);
    } catch (error) {
      toast.error("Failed to update completion status");
    }
  };

  // ─── Sharing ──────────────────────────────────────────────────

  const handleSharePlan = (plan) => {
    setPlanToShare(plan);
    setShowShareDialog(true);
  };

  // ─── Plan Detail Helpers ─────────────────────────────────────

  const handleSelectPlan = (plan) => {
    setSelectedPlan(plan);
    // Default: select the first incomplete week
    const completedDays = plan.completed_days || [];
    let firstIncomplete = 1;
    for (let i = 1; i <= (plan.duration_weeks || 8); i++) {
      const weekDays = completedDays.filter(d => d > (i - 1) * 7 && d <= i * 7);
      if (weekDays.length < 7) {
        firstIncomplete = i;
        break;
      }
    }
    setActiveWeek(firstIncomplete);
  };

  // ─── Render ──────────────────────────────────────────────────

  return (
    <div className="min-h-screen bg-gray-50 dark:bg-gray-950">
      <Navigation user={user} onLogout={onLogout} />

      <APSQAssessmentDialog
        open={showAssessment}
        onOpenChange={setShowAssessment}
        assessmentData={assessmentData}
        loading={loading}
        onAssessmentChange={handleAssessmentChange}
        onToggleArrayValue={toggleArrayValue}
        onSubmit={handleAssessmentSubmit}
      />

      <div className="max-w-7xl mx-auto px-3 sm:px-6 lg:px-8 py-4 sm:py-8">
        {/* Header */}
        <div className="flex flex-col sm:flex-row sm:items-start justify-between gap-3 mb-6 sm:mb-8">
          <div>
            <h1 data-testid="training-plans-title" className="text-2xl sm:text-3xl lg:text-4xl font-bold text-gray-900 tracking-tight">
              My Training Plans
            </h1>
            <p className="text-xs sm:text-sm text-gray-500 mt-1">
              Personalized plans based on your APSQ assessment
            </p>
          </div>
          <div className="flex gap-2">
            <Button
              data-testid="renew-plan-btn"
              onClick={async () => {
                setRenewingPlan(true);
                try {
                  const token = localStorage.getItem("token");
                  const res = await axios.post(`${API}/training-plans/renew`, {}, {
                    headers: { Authorization: `Bearer ${token}` },
                  });
                  toast.success("New plan generating based on your check-in data!");
                  // Poll for completion
                  const pollId = res.data.plan_id;
                  const poll = setInterval(async () => {
                    try {
                      const check = await axios.get(`${API}/ai/plans`, {
                        headers: { Authorization: `Bearer ${token}` },
                      });
                      const found = check.data.find(p => p.id === pollId);
                      if (found && found.generation_status === "complete") {
                        clearInterval(poll);
                        setRenewingPlan(false);
                        fetchPlans();
                        toast.success("Updated plan is ready!");
                      }
                    } catch (err) { console.error("Poll error:", err); }
                  }, 3000);
                  setTimeout(() => { clearInterval(poll); setRenewingPlan(false); }, 60000);
                } catch (e) {
                  const msg = e.response?.data?.detail || "Failed to generate updated plan";
                  toast.error(msg);
                  setRenewingPlan(false);
                }
              }}
              disabled={renewingPlan}
              className="bg-indigo-600 hover:bg-indigo-700 text-xs sm:text-sm font-semibold shadow-sm h-10"
            >
              {renewingPlan ? (
                <><Loader2 className="w-4 h-4 animate-spin" /><span className="ml-1">Generating...</span></>
              ) : (
                <><RefreshCw className="w-4 h-4" /><span className="hidden sm:inline ml-1">Update Plan</span><span className="sm:hidden ml-1">Update</span></>
              )}
            </Button>
            <Button
              data-testid="generate-plan-btn"
              onClick={() => setShowAssessment(true)}
              className="bg-gradient-to-r from-emerald-600 to-teal-600 hover:from-emerald-700 hover:to-teal-700 text-xs sm:text-sm font-semibold shadow-sm h-10"
            >
              <ClipboardList className="w-4 h-4 sm:w-5 sm:h-5" />
              <span className="hidden sm:inline ml-1">Complete Assessment</span>
              <span className="sm:hidden ml-1">Assess</span>
            </Button>
            <Button
              data-testid="manual-create-plan-btn"
              onClick={() => setShowManualCreate(true)}
              variant="outline"
              className="border-emerald-600 text-emerald-700 hover:bg-emerald-50 text-xs sm:text-sm shadow-sm h-10"
            >
              <FileText className="w-4 h-4 sm:w-5 sm:h-5" />
              <span className="hidden sm:inline ml-1">Create Manually</span>
              <span className="sm:hidden ml-1">Create</span>
            </Button>
          </div>
        </div>

        {/* Weekly Completion - MOVED ABOVE PLANS */}
        <div className="mb-6">
          <WeeklyCompletionTracker
            trainingPlans={trainingPlans}
            planCompletions={planCompletions}
          />
        </div>

        {/* Plans Grid - compact */}
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3 sm:gap-4 mb-8 sm:mb-10">
          {plans.map((plan) => (
            <PlanCard
              key={plan.id}
              plan={plan}
              canEdit={canUserEditPlan(plan)}
              onSelect={handleSelectPlan}
              onEdit={handleEditPlan}
              onDelete={handleDeletePlan}
              onShare={handleSharePlan}
              completionData={{
                total_completed: Array.isArray(plan.completed_days) ? plan.completed_days.length : 0
              }}
            />
          ))}
          {plans.length === 0 && (
            <EmptyPlansState onGenerateClick={() => setShowAssessment(true)} />
          )}
        </div>

        {/* Shared Plans Section */}
        <SharedPlansSection refreshKey={shareRefreshKey} />

        {/* Progress Summary (Streak, Consistency, Level, Wellness Radar, Growth) */}
        <div className="mb-8">
          <ProgressSummary />
        </div>

        {/* ─── Full Wellness Trends (same data as Trends page) ─── */}
        <div className="mb-12" data-testid="plans-wellness-section">
          <div className="flex items-center justify-between mb-4">
            <h2 className="text-lg font-bold text-gray-900">My Mental Performance Insights</h2>
            <div className="flex items-center gap-1.5">
              <Calendar className="w-3.5 h-3.5 text-gray-400" />
              <select
                data-testid="wellness-time-select"
                value={wellnessTimeRange}
                onChange={(e) => setWellnessTimeRange(e.target.value)}
                className="border border-gray-200 rounded-lg px-2.5 py-1.5 text-xs font-medium focus:outline-none focus:ring-2 focus:ring-emerald-500/40 bg-white"
              >
                <option value="7">7 days</option>
                <option value="14">14 days</option>
                <option value="30">30 days</option>
                <option value="60">60 days</option>
              </select>
            </div>
          </div>

          {/* Personal Records */}
          <div className="mb-4">
            <PersonalRecords individualTrends={wellnessTrends} />
          </div>

          {/* Key Metrics */}
          <div className="mb-4">
            <KeyMetricsCards individualTrends={wellnessTrends} />
          </div>

          {/* Radar + Growth side by side */}
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-4 mb-4">
            <WellnessRadarProfile individualTrends={wellnessTrends} title="My Mental Performance Profile" />
            <WellnessGrowthTrends individualTrends={wellnessTrends} />
          </div>

          {/* Weekly Comparison */}
          <div className="mb-4">
            <WeeklyComparison individualTrends={wellnessTrends} />
          </div>

          {/* Rolling Average Trend */}
          <div className="mb-4">
            <RollingAverageTrend individualTrends={wellnessTrends} />
          </div>

          {/* Stress-Performance + Day Patterns */}
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
            <StressPerformanceCorrelation individualTrends={wellnessTrends} />
            <DayOfWeekPatterns individualTrends={wellnessTrends} />
          </div>
        </div>
      </div>

      {/* Dialogs */}
      <ManualCreatePlanDialog
        open={showManualCreate}
        onOpenChange={setShowManualCreate}
        formData={manualFormData}
        loading={loading}
        onFormChange={setManualFormData}
        onSubmit={handleManualCreatePlan}
        onLoadTemplate={handleLoadTemplate}
      />

      <EditPlanDialog
        open={showEditPlan}
        onOpenChange={setShowEditPlan}
        formData={manualFormData}
        loading={loading}
        onFormChange={setManualFormData}
        onSubmit={handleUpdatePlan}
      />

      <DeleteConfirmDialog
        open={showDeleteConfirm}
        onOpenChange={setShowDeleteConfirm}
        plan={planToDelete}
        loading={loading}
        onConfirm={confirmDeletePlan}
      />

      <PlanDetailDialog
        plan={selectedPlan}
        onClose={() => setSelectedPlan(null)}
        activeWeek={activeWeek}
        onSetActiveWeek={setActiveWeek}
        onMarkDayComplete={markDayComplete}
      />

      <SharePlanDialog
        open={showShareDialog}
        onOpenChange={setShowShareDialog}
        plan={planToShare}
        onShared={() => setShareRefreshKey(k => k + 1)}
      />
    </div>
  );
}
